import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { EbwModalComponent } from '../ebw-modal/ebw-modal.component';
import { EBWModalMessage, EBWModalMessageCustom, EBWModalType } from '../model/ebw-modal.model';
import { safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';

/** Servizio per gestire le modali */
@Injectable({
  providedIn: 'root'
})
export class EbwModalService {
  /** Costruttore del servizio */
  constructor(private dialog: MatDialog) {}

  openDialog(componentMessage: EBWModalMessageCustom, options?: MatDialogConfig): MatDialogRef<EbwModalComponent> {
    let dialogRef = null;

    if (options) {
      // If no class is specified, use our custom class as default (defined in libs\ng-components\styling\src\lib\styles\reset.scss )
      options.panelClass = options.panelClass ? options.panelClass : 'custom-dialog-container';
      dialogRef = this.dialog.open(EbwModalComponent, options);
    } else {
      dialogRef = this.dialog.open(EbwModalComponent, { panelClass: 'custom-dialog-container' });
    }

    dialogRef.componentInstance.message = componentMessage;

    return dialogRef;
  }

  openInfoModal(message: EBWModalMessage): MatDialogRef<EbwModalComponent> {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Info,
      content: message.content,
      title: message.title
    };

    let options: MatDialogConfig = {
      autoFocus: false,
      restoreFocus: false
    };

    return this.openDialog(componentMessage, options);
  }

  openWarningModal(message: EBWModalMessage) {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Warning,
      content: message.content,
      title: message.title
    };

    let options: MatDialogConfig = {
      autoFocus: false,
      restoreFocus: false
    };

    return this.openDialog(componentMessage, options);
  }

  openErrorModal(message: EBWModalMessage) {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Error,
      content: message.content,
      title: message.title
    };

    let options: MatDialogConfig = {
      width: '30%',
      autoFocus: false,
      restoreFocus: false
    };

    return this.openDialog(componentMessage, options);
  }

  /*
  After the dialog is closed:
     dialogRef.afterClosed().subscribe(result => {
        console.log(`Dialog result: ${result}`);
     });
  */
  openConfirmModal(message: EBWModalMessage) {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Confirm,
      content: message.content,
      title: message.title
    };

    let options: MatDialogConfig = {
      autoFocus: false,
      restoreFocus: false,
      disableClose: true
    };

    return this.openDialog(componentMessage, options);
  }

  openSuccessModal(message: EBWModalMessage) {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Success,
      content: message.content,
      title: message.title
    };

    let options: MatDialogConfig = {
      autoFocus: false,
      restoreFocus: false
    };

    return this.openDialog(componentMessage, options);
  }

  openCustomModal(
    message: EBWModalMessage,
    component: Type<unknown>,
    options?: MatDialogConfig,
    customButton?: Type<unknown>
  ) {
    let componentMessage: EBWModalMessageCustom = {
      type: EBWModalType.Custom,
      content: message.content,
      title: message.title
    };

    let received_data = !!options.data ? safeJsonClone(options.data) : null;

    delete options.data;

    // If the modal width is not specified in the options, then it is set to 60vw
    options.width = options.width ?? '60vw';

    options.disableClose = options.disableClose ?? false;

    let config: MatDialogConfig = {
      autoFocus: false,
      restoreFocus: false,
      data: {
        component: component,
        ...received_data
      },
      ...options
    };

    if (customButton) {
      config.data['customButton'] = customButton;
    }

    return this.openDialog(componentMessage, config);
  }

  closeAllDialogs() {
    this.dialog.closeAll();
  }
}
